import type { CSSProperties } from 'react';
import { intelPageCssVars } from '../intel/Config';

/** 搜索框占用总高度 = offsetTop + height + offsetBottom */
export function calcInstHotspotSearchBarBlockHeight(
  searchStyle: Pick<typeof instHotspotSearchStyle, 'offsetTop' | 'height' | 'offsetBottom'> = instHotspotSearchStyle
): number {
  return searchStyle.offsetTop + searchStyle.height + searchStyle.offsetBottom;
}

export const instHotspotLayout = {
  /** 左侧主题栏宽度（px） */
  leftWidth: 240,
  centerWidth: 600,
  /** 三栏内容区距视口底部的留白（px），调小则区域向下延伸、调大则上移 */
  contentBottomGap: 16,
  /** 栏间分割线宽度（px），设为 0 时不显示 */
  columnDividerWidth: 0,
  /** 栏间分割线颜色 */
  columnDividerColor: '#e8e8e8',
} as const;

function instHotspotColumnDividerCssVars(
  dividerWidth: number,
  dividerColor: string
): Record<string, string> {
  return {
    ['--lf-ih-column-divider-width' as string]: `${dividerWidth}px`,
    ['--lf-ih-column-divider-color' as string]: dividerColor,
  };
}

/** 页面三栏布局 CSS 变量 */
export function instHotspotLayoutCssVars(): CSSProperties {
  const {
    leftWidth,
    centerWidth,
    contentBottomGap,
    columnDividerWidth,
    columnDividerColor,
  } = instHotspotLayout;
  const rightPanelWidth = calcInstHotspotRightPanelWidth();
  const totalWidth = leftWidth + centerWidth + rightPanelWidth;
  return {
    ...instHotspotColumnDividerCssVars(columnDividerWidth, columnDividerColor),
    ['--lf-ih-layout-total-width' as string]: `${totalWidth}px`,
    ['--lf-ih-layout-left-width' as string]: `${leftWidth}px`,
    ['--lf-ih-layout-center-width' as string]: `${centerWidth}px`,
    ['--lf-ih-layout-right-width' as string]: `${rightPanelWidth}px`,
    ['--lf-ih-content-bottom-gap' as string]: `${contentBottomGap}px`,
  };
}

/** 左侧主题、中间快报选中态共用样式 */
export const instHotspotActiveStyle = {
  /** 选中背景色 */
  backgroundColor: 'rgb(255, 237, 253)',
} as const;

/** 左/中/右栏区标题共用样式（改 color 一处，三栏标题同步） */
export const instHotspotColumnSectionTitleStyle = {
  color: 'rgb(159, 159, 159)',
  fontSize: 14,
  fontWeight: 400,
  marginBottom: 12,
} as const;

/** 左侧排行榜区标题 */
export const instHotspotLeftPanelStyle = {
  sectionTitle: '热度排名',
} as const;

/** 搜索结果列表区标题 */
export const instHotspotSearchResultStyle = {
  /** 搜索关键词高亮背景色 */
  keywordHighlightBackground: '#fff566',
} as const;

/** 左侧主题卡片样式 */
export const instHotspotThemeCardStyle = {
  borderRadius: 8,
  backgroundColor: ' #fafafa',
  activeBackgroundColor: instHotspotActiveStyle.backgroundColor,
  padding: '12px 14px',
  marginBottom: 10,
  cursor: 'pointer',
  /** 卡片内左侧序号数字区域宽度（px） */
  rankColumnWidth: 30,
  /** 排序序号颜色 */
  rankColor: '#333',
  /** 排序序号字号（px） */
  rankFontSize: 20,
  /** 排序序号金融字体 */
  rankFontFamily: '"DIN Medium", "JetBrains Mono", ui-monospace, monospace',
  /** 热度/密度小块间距（px） */
  metricBlockGap: 0,
  /** 主题名与数值区之间的上内边距（px） */
  themeMetricsPaddingTop: 12,
  titleColor: 'rgb(6, 121, 229)',
  /** 主题名字号（px） */
  titleFontSize: 15,
  /** 热度/密度标签文字颜色 */
  metaLabelColor: '#888',
  /** 热度/密度标签字号（px） */
  metaLabelFontSize: 12,
  /** 热度说明提示 */
  heatTooltip: '最近机构研究的热度。',
  /** 密度说明提示 */
  densityTooltip: '最近一个月机构研究的频繁度。',
  /** 指标问号图标字号（px） */
  metricHelpIconSize: 12,
  /** 指标问号图标颜色 */
  metricHelpIconColor: 'rgba(0, 0, 0, 0.35)',
  /** 热度数值颜色 */
  heatValueColor: '#f5222d',
  /** 密度数值颜色 */
  densityValueColor: 'rgb(248, 136, 8)',
  /** 热度/密度数值字号（px） */
  metricValueFontSize: 18,
  /** 热度/密度数值金融字体 */
  metricValueFontFamily: '"DIN Medium", "JetBrains Mono", ui-monospace, monospace',
} as const;

/** 主题卡片 CSS 变量（序号区宽度、主题与数值区间距等） */
export function instHotspotThemeCardCssVars(): CSSProperties {
  const s = instHotspotThemeCardStyle;
  return {
    ['--lf-ih-theme-rank-width' as string]: `${s.rankColumnWidth}px`,
    ['--lf-ih-theme-metrics-padding-top' as string]: `${s.themeMetricsPaddingTop}px`,
  };
}

export const instHotspotTimelineStyle = {
  /** 条目间距（px） */
  itemGap: 12,
  /** 条目底部内边距（px） */
  itemPaddingBottom: 16,
  /** 选中条目背景色（与左侧主题选中态一致，改 instHotspotActiveStyle.backgroundColor） */
  activeBackgroundColor: instHotspotActiveStyle.backgroundColor,
  /** 标题字号（px） */
  titleFontSize: 14,
  /** 标题字重 */
  titleFontWeight: 400,
  /** 标题颜色 */
  titleColor: '#333',
  /** 日期颜色 */
  dateLabelColor: 'rgb(48, 48, 48)',
  /** 日期字号（px） */
  dateLabelFontSize: 13,
  /** 标题与日期间距（px） */
  titleDateGap: 4,
  /** 日期与正文间距（px） */
  dateContentGap: 8,
  /** 正文字号（px） */
  contentFontSize: 14,
  /** 正文颜色 */
  contentColor: 'rgb(90, 90, 90)',
  /** 正文字重 */
  contentFontWeight: 400,
  /** Markdown **高亮** 文字颜色 */
  markdownHighlightColor: 'rgb(254, 79, 222)',
  /** Markdown **高亮** 字重 */
  markdownHighlightFontWeight: 400,
  /** 「· 关联个股 x」文案颜色 */
  referStockCountColor: 'rgb(81, 130, 245)',
  /** 「· 关联个股 x」文案字号（px） */
  referStockCountFontSize: 13,
} as const;

/** 时间线条目 CSS 变量（日期/标题/正文等，改 Config 后刷新即可生效） */
export function instHotspotTimelineCssVars(): CSSProperties {
  const s = instHotspotTimelineStyle;
  return {
    ['--lf-ih-timeline-title-font-size' as string]: `${s.titleFontSize}px`,
    ['--lf-ih-timeline-title-font-weight' as string]: String(s.titleFontWeight),
    ['--lf-ih-timeline-title-color' as string]: s.titleColor,
    ['--lf-ih-timeline-date-color' as string]: s.dateLabelColor,
    ['--lf-ih-timeline-date-font-size' as string]: `${s.dateLabelFontSize}px`,
    ['--lf-ih-timeline-title-date-gap' as string]: `${s.titleDateGap}px`,
    ['--lf-ih-timeline-date-content-gap' as string]: `${s.dateContentGap}px`,
    ['--lf-ih-timeline-content-font-size' as string]: `${s.contentFontSize}px`,
    ['--lf-ih-timeline-content-color' as string]: s.contentColor,
    ['--lf-ih-timeline-content-font-weight' as string]: String(s.contentFontWeight),
    ['--lf-ih-timeline-refer-stock-color' as string]: s.referStockCountColor,
    ['--lf-ih-timeline-refer-stock-font-size' as string]: `${s.referStockCountFontSize}px`,
  };
}

/** 搜索框样式 */
export const instHotspotSearchStyle = {
  maxWidth: 580,
  height: 40,
  borderRadius: 6,
  pageBackgroundColor: '#ffffff',
  inputBackgroundColor: '#f5f5f5',
  placeholder: '输入研报关键词',
  textColor: '#333',
  placeholderColor: '#aaa',
  iconColor: 'rgba(0, 0, 0, 0.35)',
  iconHoverColor: 'rgba(0, 0, 0, 0.55)',
  /** 搜索框距页面顶部（px） */
  offsetTop: 32,
  /** 搜索框距下方内容展示区（px） */
  offsetBottom: 16,
} as const;

/** 搜索栏间距 CSS 变量 */
export function instHotspotSearchCssVars(): CSSProperties {
  const s = instHotspotSearchStyle;
  const searchBarBlockHeight = calcInstHotspotSearchBarBlockHeight(s);
  return {
    ['--lf-ih-search-offset-top' as string]: `${s.offsetTop}px`,
    ['--lf-ih-search-offset-bottom' as string]: `${s.offsetBottom}px`,
    ['--lf-ih-search-block-height' as string]: `${searchBarBlockHeight}px`,
  };
}

/** 搜索结果页左右布局 */
export const instHotspotSearchLayout = {
  leftWidth: 700,
  /** 搜索内容区距视口底部的留白（px），与 instHotspotLayout.contentBottomGap 独立可调 */
  contentBottomGap: 16,
  /** 搜索内容区（stage）左右内边距（px），与 .lf-inst-hotspot-search-stage padding 保持一致 */
  stagePaddingX: 12,
  /** 栏间分割线宽度（px），设为 0 时不显示；与 instHotspotLayout 保持一致即可 */
  columnDividerWidth: 0,
  columnDividerColor: '#e8e8e8',
} as const;

export function instHotspotSearchLayoutCssVars(): CSSProperties {
  const { leftWidth, contentBottomGap, stagePaddingX, columnDividerWidth, columnDividerColor } =
    instHotspotSearchLayout;
  const rightPanelWidth = calcInstHotspotRightPanelWidth();
  const totalWidth = leftWidth + rightPanelWidth + stagePaddingX * 2;
  return {
    ...instHotspotColumnDividerCssVars(columnDividerWidth, columnDividerColor),
    ['--lf-ih-search-layout-left-width' as string]: `${leftWidth}px`,
    ['--lf-ih-search-layout-right-width' as string]: `${rightPanelWidth}px`,
    ['--lf-ih-search-layout-total-width' as string]: `${totalWidth}px`,
    ['--lf-ih-search-stage-padding-x' as string]: `${stagePaddingX}px`,
    ['--lf-ih-search-content-bottom-gap' as string]: `${contentBottomGap}px`,
  };
}

/** 搜索结果快报卡片样式 */
export const instHotspotSearchCardStyle = {
  borderRadius: 8,
  backgroundColor: '#fafafa',
  activeBackgroundColor: instHotspotActiveStyle.backgroundColor,
  padding: '14px 16px',
  marginBottom: 12,
  cursor: 'pointer',
  themeNameColor: 'rgb(6, 121, 229)',
  themeNameFontSize: 13,
  footerBorderColor: '#ececec',
  footerPaddingTop: 12,
  footerMarginTop: 12,
  /** 底部热度/密度数值相对 label 垂直偏移（px，正值下移，补偿 DIN 字体视觉偏高） */
  footerMetricValueOffsetY: 2,
} as const;

/** 右侧关联个股区样式 */
export const instHotspotRightPanelStyle = {
  /** 顶部固定标题文案 */
  sectionTitle: '提及股票',
  /** 右栏（提及股票）固定总宽度（px，含 padding，box-sizing: border-box） */
  width: 332,
  /** 右栏水平内边距（px），与 .lf-inst-hotspot-col--right padding 保持一致 */
  paddingX: 16,
} as const;

/** 右侧关联个股卡片样式 */
export const instHotspotStockCardStyle = {
  /** 卡片宽度（px）；为 null 时占满右栏内容区 100% */
  width: null as number | null,
  /** 标题行（股票名 + 行情）上内边距（px） */
  titlePaddingTop: 6,
  /** 标题行（股票名 + 行情）下内边距（px） */
  titlePaddingBottom: 6,
  backgroundColor: '#ffffff',
  hoverBackgroundColor: '#fafafa',
} as const;

/** 右栏固定宽度，直接取 instHotspotRightPanelStyle.width */
export function calcInstHotspotRightPanelWidth(): number {
  return instHotspotRightPanelStyle.width;
}

/** 右侧栏 CSS 变量（复用情报卡片结构，背景改为白底） */
export function instHotspotRightPanelCssVars(): CSSProperties {
  const { paddingX } = instHotspotRightPanelStyle;
  const { width: stockCardWidth, titlePaddingTop, titlePaddingBottom, backgroundColor, hoverBackgroundColor } =
    instHotspotStockCardStyle;
  return {
    ...intelPageCssVars(),
    ['--lf-ih-right-panel-padding-x' as string]: `${paddingX}px`,
    ['--lf-ih-stock-card-width' as string]: stockCardWidth == null ? '100%' : `${stockCardWidth}px`,
    ['--lf-intel-stock-head-padding-top' as string]: `${titlePaddingTop}px`,
    ['--lf-intel-stock-head-padding-bottom' as string]: `${titlePaddingBottom}px`,
    ['--lf-intel-stock-card-bg' as string]: backgroundColor,
    ['--lf-intel-stock-card-hover-bg' as string]: hoverBackgroundColor,
  };
}
